import { pageSizes } from "../config";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ExecutorCell } from "../components/ExecutorCell";
import { downReadText, PageBody } from "../components/PageBody";
import { PageHead } from "../components/PageHead";
import { NewAbove, Pager } from "../components/Pager";
import { hoursText } from "../components/limitRows";
import { Card, CopyButton, ExplorerLink, isChainDown, Loading } from "../components/ui";
import { shortAddr } from "../lib/address";
import { api, type MakerChain, type MonitoringAnswer, type TransactionRow, type TransactionType, type VenueExecutor } from "../lib/api";
import { venueWords } from "../lib/venueExecutors";
import { EVENT_NAMES } from "../lib/events";
import { sentWords } from "../lib/telegramChats";
import { addressUrl, txUrl } from "../lib/explorer";
import { useNetwork } from "../lib/network";
import { useReadTime } from "../lib/readTime";
import { utcDayTime } from "../lib/time";
import { formatAllowance, formatToken, formatUsd } from "../lib/units";
import { useCursorList } from "../lib/useCursorList";
import { useLoad } from "../lib/useLoad";
import "./MonitoringPage.css";

const ROLES: Record<MonitoringAnswer["contracts"][number]["role"], { name: string; sub: string; order: number }> = {
  executor: { name: "Your executor", sub: "The same address on every network", order: 0 },
  owner: { name: "Owner wallet", sub: "Signs your executor’s limits", order: 1 },
  treasury: { name: "Treasury", sub: "Pays and receives your fills", order: 2 },
  filler: { name: "AileFX filler", sub: "Sends your fills", order: 3 },
};

const TX_TYPES: Record<TransactionRow["type"], string> = { fill: "Fill", "limit-change": "Limit change", allowance: "Allowance" };
const NO_ROWS: Record<TransactionType, string> = { all: "transactions", fill: "fills", "limit-change": "limit changes", allowance: "allowances" };
const PAGE = pageSizes.transactions;

/** A summary with its full addresses shortened. */
export function shortSummary(summary: string): string {
  return summary.replace(/0x[0-9a-fA-F]{40}/g, (a) => shortAddr(a));
}

/** "40000.5" (a decimal string, never a float) as "40,000.50". */
export function groupedDecimal(text: string): string {
  const [whole = "0", frac = ""] = text.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${grouped}.${(frac + "00").slice(0, Math.max(2, frac.replace(/0+$/, "").length))}`;
}

/** "40000 USDC" as "40,000.00 USDC"; anything else as it came. */
function amountWords(value: string): string {
  const m = /^(\d+(?:\.\d+)?) (\S+)$/.exec(value);
  return m === null ? value : `${groupedDecimal(m[1]!)} ${m[2]!}`;
}

const onOffWord = (v: string) => (v === "on" ? "On" : v === "off" ? "Off" : v);

/**
 * An owner call's summary in words. The API joins its lines as "label: value;
 * label: value"; each known line reads as a sentence, a pair bound's four
 * lines as one, and a line of any other shape stays as it came.
 */
export function ownerCallWords(summary: string): string {
  const lines = summary.split("; ").map((part) => {
    const at = part.indexOf(": ");
    return at === -1 ? { label: part, value: "" } : { label: part.slice(0, at), value: part.slice(at + 2) };
  });
  // A pair bound's four lines become one entry, in the place of its first line.
  const out: ({ text: string } | { bound: string })[] = [];
  const bounds = new Map<string, { bps?: string; sec?: string }>();
  const fills = lines.some((l) => l.label === "AileFX fills");
  for (const { label, value } of lines) {
    const bound = /^(\S+)→(\S+) (feed|inverted|max distance from the feed \(bps\)|feed max age \(s\))$/.exec(label);
    if (bound !== null) {
      const key = bound[1]!;
      if (!bounds.has(key)) {
        bounds.set(key, {});
        out.push({ bound: key });
      }
      const b = bounds.get(key)!;
      if (bound[3] === "max distance from the feed (bps)") b.bps = value;
      if (bound[3] === "feed max age (s)") b.sec = value;
      continue;
    }
    let m: RegExpExecArray | null;
    if (label === "Operator" && fills) continue;
    if (label === "AileFX fills" || label === "Maker allowlist") out.push({ text: `${label} set to ${onOffWord(value)}` });
    else if ((m = /^(\S+) daily cap$/.exec(label)) !== null) out.push({ text: `${m[1]!} daily cap set to ${amountWords(value)}` });
    else if ((m = /^(\S+) allowance to the executor$/.exec(label)) !== null) {
      const sym = m[1]!;
      out.push({
        text:
          value === "unlimited"
            ? `The treasury let your executor spend any amount of ${sym}`
            : /^0 /.test(value)
              ? `The treasury let your executor spend no ${sym}`
              : `The treasury let your executor spend ${amountWords(value)}`,
      });
    } else if (label === "Treasury" && /^0x[0-9a-fA-F]{40}$/.test(value)) out.push({ text: `Treasury set to ${shortAddr(value)}` });
    else out.push({ text: shortSummary(value === "" ? label : `${label}: ${value}`) });
  }
  return out
    .map((entry) => {
      if ("text" in entry) return entry.text;
      const sym = entry.bound;
      const b = bounds.get(sym)!;
      const parts = [b.bps === undefined ? null : `${Number(b.bps).toLocaleString("en-US")} bps`, b.sec === undefined ? null : `${hoursText(Number(b.sec))} h`].filter((x) => x !== null);
      return parts.length === 0 ? `Pair bound for customers buying ${sym} changed` : `Pair bound for customers buying ${sym}: ${parts.join(", ")}`;
    })
    .join("; ");
}

/**
 * Monitoring, the landing page: volumes and fills over 24 h and 7 days, the
 * treasury's value and balances (shown here only), the executor, the
 * transactions on chain and the latest events. Read-only.
 */
export function MonitoringPage() {
  const { chain } = useNetwork();
  const load = useLoad(
    (signal) =>
      api.monitoring(chain.chainId, { signal }).then((monitoring) => ({ monitoring, venue: null })),
    [chain.chainId],
  );
  const readAt = useReadTime(chain.key, load.data);
  return (
    <div className="console-page">
      <PageHead
        title="Monitoring"
        readAt={readAt}
        readText={downReadText(load, chain, readAt)}

      />
      <PageBody load={load} chain={chain}>
        {(data) => <MonitoringBody chain={chain} monitoring={data.monitoring} />}
      </PageBody>
    </div>
  );
}

/** "No fills yet" when none ever; for the desk, a day without fills in a week with some reads "No fills". */
const fillsSub = (count: number, others = false) => (count === 0 ? (others ? "No fills" : "No fills yet") : `${count.toLocaleString("en-US")} ${count === 1 ? "fill" : "fills"}`);

function Tiles({ monitoring, desk = false }: { monitoring: MonitoringAnswer; desk?: boolean }) {
  return (
    <div className="mon-tiles">
      <div className="mon-tile">
        <span className="mon-tile-label">Volume, 24 h</span>
        <span className="mon-tile-value">{formatUsd(monitoring.fills.day.volumeUsd)}</span>
        <span className="mon-tile-sub">{fillsSub(monitoring.fills.day.count, desk && monitoring.fills.week.count > 0)}</span>
      </div>
      <div className="mon-tile">
        <span className="mon-tile-label">Volume, 7 days</span>
        <span className="mon-tile-value">{formatUsd(monitoring.fills.week.volumeUsd)}</span>
        <span className="mon-tile-sub">{fillsSub(monitoring.fills.week.count)}</span>
      </div>
    </div>
  );
}

/** AileFX's desk: the volumes, and the transactions of every venue executor of the network. */

function MonitoringBody({ chain, monitoring }: { chain: MakerChain; monitoring: MonitoringAnswer }) {
  const contracts = [...monitoring.contracts].sort((a, b) => ROLES[a.role].order - ROLES[b.role].order);
  const treasury = monitoring.treasury;
  const decimalsOf = (token: string) => treasury.tokens.find((t) => t.token.toLowerCase() === token.toLowerCase())?.decimals ?? null;
  return (
    <>
      <Tiles monitoring={monitoring} />

      <Card
        title="Treasury balances"
        tools={
          treasury.address !== null && (
            <span className="mon-value">
              Value <span className="mon-value-amount">{treasury.valueUsd === null ? "—" : formatUsd(treasury.valueUsd)}</span>
            </span>
          )
        }
      >
        {treasury.address === null ? (
          <p className="mk-text">
            No treasury on {chain.label} yet: set it on <Link to="/treasury">Treasury</Link>.
          </p>
        ) : (
          <table className="mk-table">
            <thead>
              <tr>
                <th scope="col" style={{ width: 300 }}>
                  Coin
                </th>
                <th scope="col" className="mk-num">
                  Balance
                </th>
                <th scope="col" style={{ width: 260 }} className="mk-num">
                  ≈ USD
                </th>
              </tr>
            </thead>
            <tbody>
              {treasury.tokens.map((t) => (
                <tr key={t.token}>
                  <th scope="row" className="mk-row-name-text">
                    {t.symbol}
                  </th>
                  <td className="mk-num">{formatToken(t.balance, t.decimals, t.symbol)}</td>
                  <td className="mk-num mon-muted">{formatUsd(t.valueUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="mk-foot">
          Target shares and the suggested swap are on <Link to="/rebalancing">Rebalancing</Link>.
        </p>
      </Card>

      <Card title="Contracts and wallets">
        {contracts.length === 0 ? (
          <p className="mk-text">Nothing to show on {chain.label} yet: AileFX has not issued your executor.</p>
        ) : (
          <table className="mk-table">
            <thead>
              <tr>
                <th scope="col" style={{ width: 300 }}>
                  Role
                </th>
                <th scope="col">Address</th>
                <th scope="col" style={{ width: 140 }} className="mk-num">
                  <span className="sr-only">Explorer</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.role}>
                  <th scope="row" className="mk-mid mon-pad">
                    <span className="mk-row-name-text">{ROLES[c.role].name}</span>
                    <span className="mk-sub mon-block">{ROLES[c.role].sub}</span>
                  </th>
                  <td className="mk-mid mon-pad">
                    <span className="mk-addr">
                      <span className="mk-mono mon-addr">{shortAddr(c.address)}</span>
                      <CopyButton text={c.address} label={`Copy the address of ${ROLES[c.role].name.toLowerCase()}`} />
                    </span>
                  </td>
                  <td className="mk-num mk-mid mon-link">
                    <ExplorerLink href={addressUrl(chain, c.address)} label={`${ROLES[c.role].name} on the explorer`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Approvals">
        {monitoring.approvals.length === 0 ? (
          <p className="mk-text">Your executor is not deployed on {chain.label} yet, so the treasury lets it spend nothing.</p>
        ) : (
          <table className="mk-table">
            <thead>
              <tr>
                <th scope="col" style={{ width: 300 }}>
                  Coin
                </th>
                <th scope="col">The treasury lets spend</th>
                <th scope="col" style={{ width: 260 }} className="mk-num">
                  Allowance
                </th>
              </tr>
            </thead>
            <tbody>
              {monitoring.approvals.map((a) => {
                const decimals = decimalsOf(a.token);
                const text = decimals === null ? (a.allowance === "0" ? "None" : "—") : formatAllowance(a.allowance, decimals, a.symbol);
                return (
                  <tr key={a.token}>
                    <th scope="row" className="mk-row-name-text">
                      {a.symbol}
                    </th>
                    <td className="mon-muted">
                      Your executor <span className="mk-mono mk-strong">{shortAddr(a.spender)}</span>
                    </td>
                    <td className={text === "None" ? "mk-num mon-strong mon-warn" : "mk-num mon-strong"}>{text}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="mk-foot">
          Set from the treasury wallet on <Link to="/treasury">Treasury</Link>.
        </p>
      </Card>

      <TransactionsCard chain={chain} />

      <Card title="Recent events">
        {monitoring.events.length === 0 ? (
          <p className="mk-text">No events on {chain.label} yet.</p>
        ) : (
          <table className="mk-table">
            <thead>
              <tr>
                <th scope="col" style={{ width: 150 }}>
                  Time, UTC
                </th>
                <th scope="col" style={{ width: 150 }}>
                  Event
                </th>
                <th scope="col">Details</th>
                <th scope="col" style={{ width: 150 }} className="mk-num">
                  Telegram
                </th>
              </tr>
            </thead>
            <tbody>
              {monitoring.events.map((e) => (
                <tr key={e.id}>
                  <td className="mon-time">{utcDayTime(e.at)}</td>
                  <td className="mon-strong mon-nowrap">{EVENT_NAMES[e.kind]}</td>
                  <td className="mon-details">{e.text}</td>
                  <td className="mk-num mon-muted">{sentWords(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="mk-foot">
          Rules and the Telegram chat are on <Link to="/notifications">Notifications</Link>.
        </p>
      </Card>
    </>
  );
}

/**
 * The network's transactions, newest first, a page at a time from the server;
 * the Type select starts again at page one. For the desk (`venue`), each row
 * names its executor when the answer does, a retired one marked.
 */
function TransactionsCard({ chain, venue = null }: { chain: MakerChain; venue?: readonly VenueExecutor[] | null }) {
  const [type, setType] = useState<TransactionType>("all");
  const list = useCursorList<TransactionRow>(
    (cursor, limit, { signal, silent }) => api.transactions(chain.chainId, { limit, type, before: cursor }, { signal, silent }).then((a) => ({ rows: a.transactions, next: a.nextBefore })),
    { pageSize: PAGE, keyOf: (t) => t.id, deps: [chain.chainId, type] },
  );
  const rows = list.rows;
  const failed = list.error === null ? null : isChainDown(list.error) ? `${chain.label} is not answering, so its transactions could not be read.` : "The transactions could not be read just now.";
  // The column shows once the answer names executors.
  const byExecutor = venue !== null && rows !== null && rows.some((t) => t.executor !== undefined);
  const empty =
    venue !== null && venue.length > 0 && (type === "all" || type === "fill")
      ? `No ${NO_ROWS[type]} on ${chain.label} yet by any venue executor: ${venueWords(venue)}.`
      : `No ${NO_ROWS[type]} on ${chain.label} yet.`;
  return (
    <Card
      title="Transactions"
      tools={
        <div className="mk-filter">
          <label htmlFor="mon-type">Type</label>
          <select id="mon-type" className="mk-select mon-type" value={type} onChange={(e) => setType(e.target.value as TransactionType)}>
            <option value="all">All types</option>
            <option value="fill">Fills</option>
            <option value="limit-change">Limit changes</option>
            <option value="allowance">Allowances</option>
          </select>
        </div>
      }
    >
      <NewAbove list={list} noun="transactions" />
      {rows === null ? (
        failed === null ? (
          <Loading />
        ) : null
      ) : rows.length === 0 && list.page === 0 ? (
        <p className={venue === null ? "mk-text" : "mk-empty"}>{empty}</p>
      ) : (
        <table className="mk-table">
          <thead>
            <tr>
              <th scope="col" style={{ width: 150 }}>
                Time, UTC
              </th>
              {byExecutor && (
                <th scope="col" className="mon-col-executor">
                  Executor
                </th>
              )}
              <th scope="col" style={{ width: 150 }}>
                Type
              </th>
              <th scope="col">Details</th>
              <th scope="col" style={{ width: 120 }} className="mk-num">
                <span className="sr-only">Transaction</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id}>
                <td className="mk-mid mon-time">{utcDayTime(t.at)}</td>
                {byExecutor && (
                  <td className="mk-mid">
                    <ExecutorCell address={t.executor ?? null} venue={venue} />
                  </td>
                )}
                <td className="mk-mid mon-nowrap">{TX_TYPES[t.type] ?? (t.kind === "fill" ? TX_TYPES.fill : TX_TYPES["limit-change"])}</td>
                <td className="mk-mid mon-details">{t.kind === "owner-call" ? ownerCallWords(t.summary) : shortSummary(t.summary)}</td>
                <td className="mk-num mk-mid mon-link">
                  <ExplorerLink href={txUrl(chain, t.txHash)} label={`${TX_TYPES[t.type] ?? "Transaction"} of ${utcDayTime(t.at)} UTC`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {failed !== null && (
        <p className="mk-status-bad" role="alert">
          {failed}{" "}
          <button type="button" className="mk-btn-text" onClick={list.retry}>
            Try again
          </button>
        </p>
      )}
      {rows !== null && (rows.length > 0 || list.page > 0) && <Pager list={list} noun="transactions" />}
    </Card>
  );
}
